import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import { getLegalPageSettings, getBrandingSettings, getPortfolioByOwner } from '../../services/dataService';
import { LegalPageSettings, BrandingSettings } from '../../types';
import { useTenant } from '../../context/TenantContext';

interface GlobalFooterProps {
    hasBottomNav?: boolean;
    isSaaS?: boolean;
}

const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (document.documentElement) document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
    if (document.body) document.body.scrollTo({ top: 0, behavior: 'smooth' });
};

export const GlobalFooter: React.FC<GlobalFooterProps> = ({ hasBottomNav = false, isSaaS = false }) => {
    const { tenant } = useTenant();
    const [legal, setLegal] = useState<LegalPageSettings>({});
    const [branding, setBranding] = useState<BrandingSettings>({ appTitle: 'SyncZ' });

    useEffect(() => {
        const fetch = async () => {
            try {
                const l = await getLegalPageSettings(tenant?.id);
                setLegal(l || {});
                let b: BrandingSettings = { appTitle: 'SyncZ' };
                if (tenant && tenant.ownerId) { 
                     const portfolio = await getPortfolioByOwner(tenant.ownerId); 
                     if (portfolio) { 
                         b = { 
                              appTitle: portfolio.name, 
                              logoUrl: portfolio.logoUrl, 
                              faviconUrl: portfolio.logoUrl 
                          }; 
                     } else { 
                         b = { appTitle: tenant.name }; 
                     }
                } else {
                    b = await getBrandingSettings();
                }
                setBranding(b || { appTitle: 'SyncZ' });
            } catch(e) {}
        };
        fetch();
    }, [tenant]);

    const LegalLink = ({ to, label, url }: { to: string, label: string, url?: string }) => {
        if (url) {
            return <a href={url} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors underline decoration-white/20 underline-offset-4">{label}</a>;
        }
        return <Link to={to} className="hover:text-white transition-colors underline decoration-white/20 underline-offset-4">{label}</Link>;
    };

    if (isSaaS) {
        return (
            <footer className={`border-t border-white/5 py-16 ${hasBottomNav ? 'pb-32' : ''} bg-black relative z-10 no-print px-6`}>
                <div className="w-full mx-auto max-w-6xl">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16 border-b border-white/10 pb-12">
                        <div>
                            <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-6">Product</h4>
                            <ul className="space-y-4 text-sm text-neutral-400">
                                <li><Link to="/services" className="hover:text-white transition-colors">Services & Solutions</Link></li>
                                <li><Link to="/portfolio" className="hover:text-white transition-colors">Portfolio & Work</Link></li>
                                <li><Link to="/marketplace" className="hover:text-white transition-colors">Marketplace</Link></li>
                                <li><Link to="/contact" className="hover:text-white transition-colors">Custom Inquiries</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-6">Resources</h4>
                            <ul className="space-y-4 text-sm text-neutral-400">
                                <li><Link to="/workshop" className="hover:text-white transition-colors">Workshops & Events</Link></li>
                                <li><Link to="/about" className="hover:text-white transition-colors">Knowledge Base</Link></li>
                                <li><Link to="/contact" className="hover:text-white transition-colors">Support & Help</Link></li>
                                <li><Link to="/login" className="hover:text-white transition-colors">Client Portal</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-6">Company</h4>
                            <ul className="space-y-4 text-sm text-neutral-400">
                                <li><Link to="/about" className="hover:text-white transition-colors">About Us</Link></li>
                                <li><Link to="/cv" className="hover:text-white transition-colors">Experience & CV</Link></li>
                                <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
                                <li><Link to="/login" className="hover:text-white transition-colors">Staff Login</Link></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-white font-bold uppercase tracking-widest text-xs mb-6">Legal</h4>
                            <ul className="space-y-4 text-sm text-neutral-400">
                                <li><LegalLink to="/privacy" label="Privacy Policy" url={legal?.privacyUrl} /></li>
                                <li><LegalLink to="/terms" label="Terms of Service" url={legal?.termsUrl} /></li>
                                <li><LegalLink to="/refund-policy" label="Refund Policy" url={legal?.refundUrl} /></li>
                                <li><LegalLink to="/eula" label="EULA" url={legal?.eulaUrl} /></li>
                            </ul>
                        </div>
                    </div>
                    
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        <div className="flex items-center gap-3">
                            {(branding.logoUrl || '/logo.png') ? (
                                 <img src={branding.logoUrl || '/logo.png'} alt={branding.appTitle} className="h-6 w-auto object-contain" />
                            ) : (
                                <div className="w-6 h-6 bg-white text-black rounded-sm flex items-center justify-center font-black text-xs shrink-0">
                                    {branding.appTitle?.charAt(0) || 'S'}
                                </div>
                            )}
                            <span className="text-white font-bold text-sm tracking-widest uppercase">{branding.appTitle || 'SyncZ'}</span>
                        </div>
                        <div className="flex items-center gap-4 flex-wrap">
                            <p className="text-[10px] text-neutral-600 font-mono uppercase tracking-widest">
                                © {new Date().getFullYear()} {branding?.appTitle || 'SyncZ'}. All Rights Reserved.
                            </p>
                            <button
                                onClick={scrollToTop}
                                className="flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white transition font-mono uppercase tracking-wider py-1 px-2.5 rounded hover:bg-white/5"
                                title="Back to top"
                            >
                                <span>Back to top</span>
                                <ArrowUp size={12} />
                            </button>
                        </div>
                    </div>
                </div>
            </footer>
        );
    }

    return (
        <footer className={`border-t border-white/10 py-12 ${hasBottomNav ? 'pb-32' : ''} md:pb-12 text-center bg-black relative z-10 no-print`}>
            <div className="flex justify-center flex-wrap items-center gap-6 mb-6 text-xs font-bold uppercase tracking-widest text-neutral-400">
                <LegalLink to="/privacy" label="Privacy Policy" url={legal?.privacyUrl} />
                <LegalLink to="/terms" label="Terms of Service" url={legal?.termsUrl} />
                <LegalLink to="/refund-policy" label="Refund Policy" url={legal?.refundUrl} />
                <LegalLink to="/eula" label="EULA" url={legal?.eulaUrl} />
                <button
                    onClick={scrollToTop}
                    className="flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white transition font-mono uppercase tracking-wider py-1 px-2.5 rounded hover:bg-white/5"
                    title="Back to top"
                >
                    <span>Back to top</span>
                    <ArrowUp size={12} />
                </button>
            </div>
            <p className="text-[10px] text-neutral-600 font-mono uppercase tracking-widest mb-4">
                © {new Date().getFullYear()} {branding?.appTitle || 'SyncZ Portfolio'}. All Rights Reserved.
            </p>
        </footer>
    );
};
